// File: src/pages/auth/ResetPasswordPage.jsx
import React, { useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { authApi } from "@/lib/axios";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function strongEnough(pw = "") {
  const longEnough = pw.length >= 12;
  const hasLetter = /[A-Za-z]/.test(pw);
  const hasNumber = /\d/.test(pw);
  const hasSymbol = /[^A-Za-z0-9]/.test(pw);
  return longEnough && hasLetter && hasNumber && hasSymbol;
}

export default function ResetPasswordPage() {
  const q = useQuery();
  const params = useParams();

  // supports /reset-password?token=... OR /reset-password/:token (optional)
  const token = (q.get("token") || params.token || "").trim();

  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState({ type: "", msg: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", msg: "" });

    if (!token) {
      setStatus({ type: "error", msg: "Missing reset token. Open the link from your email." });
      return;
    }
    if (!strongEnough(newPassword)) {
      setStatus({
        type: "error",
        msg: "Password must be at least 12 characters and include letters, numbers, and a symbol.",
      });
      return;
    }
    if (newPassword !== confirm) {
      setStatus({ type: "error", msg: "Passwords do not match." });
      return;
    }

    setLoading(true);
    try {
      await authApi.post("/reset-password", {
        token,
        new_password: newPassword,
      });

      setStatus({ type: "ok", msg: "Password updated. You can now sign in." });
      setNewPassword("");
      setConfirm("");
    } catch (err) {
      setStatus({
        type: "error",
        msg:
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Reset failed",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05060f] text-white grid place-items-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/5 p-6 shadow-2xl backdrop-blur-xl">
        <h1 className="text-xl font-bold">Reset password</h1>
        <p className="mt-1 text-sm text-white/60">Choose a new password to regain access.</p>

        {!token && (
          <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
            Missing token. Open the link from your email.
          </div>
        )}

        <form onSubmit={submit} className="mt-5 space-y-3">
          <label className="block text-sm">
            <span className="text-white/80">New password</span>
            <input
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 outline-none focus:border-indigo-400"
              type="password"
              required
              disabled={!token}
            />
          </label>

          <label className="block text-sm">
            <span className="text-white/80">Confirm password</span>
            <input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 outline-none focus:border-indigo-400"
              type="password"
              required
              disabled={!token}
            />
          </label>

          {status.msg && (
            <div
              className={`rounded-lg border px-3 py-2 text-sm ${
                status.type === "ok"
                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                  : "border-red-500/30 bg-red-500/10 text-red-200"
              }`}
            >
              {status.msg}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !token}
            className={`w-full rounded-xl py-2 font-medium transition ${
              loading || !token
                ? "cursor-not-allowed bg-indigo-900/40"
                : "bg-gradient-to-r from-indigo-600 via-purple-600 to-emerald-600"
            }`}
          >
            {loading ? "Updating…" : "Update password"}
          </button>
        </form>

        <p className="mt-4 text-sm text-white/60">
          Back to{" "}
          <Link className="text-indigo-300 underline underline-offset-2" to="/login">
            sign in
          </Link>
        </p>
      </div>
    </div>
  );
}